import { useApolloClient } from '@apollo/client';
import { ListItem, ListItemText } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useBooleanState } from 'react-use-object-state';
import { Entity } from '../../../../types';
import { EntityRawDataVersionsLegacy } from '../../../graphql/queries';
import RawDataProgress from './RawDataProgress';
import { RawDataVersion, RawDataVersionsQueryResponse } from './types';

interface Props {
  entity: Entity;
  selectedRawDataObject?: string;
  onVersionsChange: (versions: RawDataVersion[]) => void;
  onSelectedVersionChange: (selectedVersion: string) => void;
}

export default function RawDataVersionsLoader({
  entity,
  selectedRawDataObject,
  onVersionsChange,
  onSelectedVersionChange,
}: Props) {
  const client = useApolloClient();

  const loading = useBooleanState(false);
  const [versions, setVersions] = useState<RawDataVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>();

  function handleSelectedVersionChange(selectedVersion: string) {
    setSelectedVersion(selectedVersion);
    onSelectedVersionChange(selectedVersion);
  }

  useEffect(() => {
    const fetchRawDataVersions = async () => {
      loading.setTrue();

      const versionsQueryResult = await client.query({
        context: {
          mute: true, // Don't show toast error, we'll handle the error
        },
        query: EntityRawDataVersionsLegacy,
        variables: {
          entityId: entity.properties._id,
          source:
            entity.properties._integrationInstanceId ||
            entity.properties._source,
          name: selectedRawDataObject,
        },
        fetchPolicy: 'cache-first',
      });

      const entityVersionData = versionsQueryResult.data
        .entityRawDataVersionsLegacy as RawDataVersionsQueryResponse;
      if (!entityVersionData) {
        loading.setFalse();
        return;
      }

      setVersions(entityVersionData.versions);
      onVersionsChange(entityVersionData.versions);

      if (entityVersionData.versions.length > 0) {
        handleSelectedVersionChange(entityVersionData.versions[0].versionId);
      }

      loading.setFalse();
    };

    if (selectedRawDataObject) {
      fetchRawDataVersions();
    }
  }, [entity, selectedRawDataObject]);

  if (loading.state) {
    return <RawDataProgress />;
  }

  if (versions.length === 0) {
    return (
      <ListItem>
        <ListItemText primary="--" />
      </ListItem>
    );
  }

  return (
    <>
      {versions.map((v) => (
        <ListItem
          key={v.versionId}
          button
          selected={selectedVersion === v.versionId}
          onClick={() => handleSelectedVersionChange(v.versionId)}
        >
          <ListItemText
            primary={new Date(v.lastModifiedTimestamp).toLocaleString()}
          />
        </ListItem>
      ))}
    </>
  );
}
